import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister
        ? formData
        : { email: formData.email, password: formData.password };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(response.data.admin));
      
      toast.success(isRegister ? 'Account created successfully!' : 'Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Authentication failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center px-4"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <img 
            src="/images/urbancial-logo.png" 
            alt="URBANCIAL - Commercial Properties" 
            className="h-24 w-auto mx-auto mb-4"
          />
          <p className="text-white/60 text-sm mt-2">Admin Portal</p>
        </div>

        {/* Form */}
        <div className="bg-white p-8 shadow-2xl rounded-2xl">
          <h2 className="text-xl font-bold text-[#0F172A] mb-6">
            {isRegister ? 'Create Admin Account' : 'Admin Login'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegister && (
              <div>
                <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-2 block">
                  Name
                </label>
                <div className="relative">
                  <Input
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    required={isRegister}
                    className="pl-10 rounded-lg"
                    data-testid="admin-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-2 block">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10 rounded-lg"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-10 rounded-lg"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="admin-submit-btn"
            >
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Login'}
            </Button>
          </form>

        </div>

        {/* Back to Site */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="text-white/60 text-sm hover:text-[#10B981] transition-colors"
          >
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
